import LinearProgress from "@mui/material/LinearProgress";
import React, { useState } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useDispatch, useSelector } from "react-redux";
import {
    Button, Container, Row, Col,
} from "react-bootstrap";
import getBrandName from "app/shared/utils/BrandHelper";
import logging from "app/shared/utils/logging";
import { useCreateAccountMutation, useGetUserDetailsMutation } from "app/services/api/mutateGateAPI";
import { decrypt, setKeyPersist } from "app/shared/utils/EncryptionUtility";
import {
    setCredentials,
    setWelcomeModalOpen,
    updateSignUpForm,
    setAlreadySignUpErrorMsg,
    setRedirectToMyAccount,
    setShowGlobalAlert,
} from "app/store/slices/authSlice";
import { UserDetailResponse, UserDetailResponseType } from "app/features/Login/types/UserDetailResponseType";
import { updateUserDetail } from "app/store/slices/userSlice";
import gRecaptchaManager from 'app/features/GRecaptcha/GRecaptchaManager';
import {ErrorResponse, LoginResponse} from "app/features/Login/types/AuthResponseType";
import NotificationBanner from "app/shared/components/NotificationBanner";
import Constants, { DateFormat } from "app/shared/constants/constants";
import { formatDate } from "app/shared/utils/helperFunctions";
import { sendToDataLayer } from "app/shared/utils/utilityFunctions";
import { RootState } from "../../store";
import { DataLayerItem, SignupSuccessEvent } from "../../types/DataLayerType";
import type { IPreferences } from "./types/SignUpForm";
import useGA4Layer from "../../shared/hooks/useGA4Layer";

type Props = {
    signIn: (success: boolean) => void,
};

const SignupFooter: React.FC<Props> = ({ signIn }) => {
    const ga4Layer = useGA4Layer();
    const appInsights = useAppInsightsContext();
    if (appInsights) {
        appInsights.trackPageView({
            name: "SignupFooter",
        });
        const metricData = {
            average: 5,
            name: "React Component Engaged Time (seconds)",
            sampleCount: 1,
        };
        const additionalProperties = { "Component Name": 'SignupFooter' };
        appInsights.trackMetric(metricData, additionalProperties);
    }

    const [createAccount, { isLoading }] = useCreateAccountMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hasEnrolledInDifferentProgram, setHasEnrolledInDifferentProgram] = useState(false);

    const [getUserDetails, { isLoading: userDataLoading }] = useGetUserDetailsMutation();

    const dispatch = useDispatch();

    const {
        brandName,
        appSource,
    } = getBrandName();

    const {
        isSignUpFormInvalid,
        signUpForm,
    } = useSelector((state: RootState) => state.auth);

    const populateUserDetails = async (aud: string) => {
        try {
            const response: UserDetailResponse = await getUserDetails({uid: aud}).unwrap();
            const tempValue: string = decrypt(response.value.toString());
            // eslint-disable-next-line
            const userDetails: UserDetailResponseType = JSON.parse(tempValue)
            dispatch(updateUserDetail(userDetails));
        } catch (e) {
            logging.error(appInsights, "_LOGIN_ERR", "error populating user details", e);
        }
    };

    const resetSignupForm = () => {
        dispatch(updateSignUpForm({
            firstName: '',
            lastName: '',
            emailAddress: '',
            memberPhone: '',
            dob: '',
            zipCode: '',
            password: '',
            confirmPassword: '',
            isSpecialOfferChecked: false,
            isCateringChecked: false,
            isSmsChecked: false,
        }));
    };

    const mapPreferences = () => {
        const preferencesPayload: IPreferences = {
            bruProgram: false,
            nnybCateringEmail: false,
            cateringEmail: false,
            ebbProgram: false,
            nnybProgram: false,
            bruCateringEmail: false,
            perksEmail: false,
            bruEmail: false,
            ebbEmail: false,
            perksProgram: false,
            nnybEmail: false,
        };

        if (brandName === Constants.EINSTEIN_APP) {
            preferencesPayload.ebbEmail = signUpForm.isSpecialOfferChecked;
            preferencesPayload.cateringEmail = signUpForm.isCateringChecked;
            preferencesPayload.ebbProgram = true;
        } else if (brandName === Constants.BRUEGGERS_APP) {
            preferencesPayload.bruEmail = signUpForm.isSpecialOfferChecked;
            preferencesPayload.bruCateringEmail = signUpForm.isCateringChecked;
            preferencesPayload.bruProgram = true;
        } else if (brandName === Constants.NOAH_APP) {
            preferencesPayload.nnybEmail = signUpForm.isSpecialOfferChecked;
            preferencesPayload.nnybCateringEmail = signUpForm.isCateringChecked;
            preferencesPayload.nnybProgram = true;
        }

        return preferencesPayload;
    };

    const onSubmit = () => {
        // eslint-disable-next-line
        onSignUpFormSubmit();
    };

    const sendSignupSuccessEvenToDataLayer = () => {
        const signupSuccessEvent: SignupSuccessEvent = {
             event: 'signup_success',
             form_name: 'Rewards',
             form_id: 'signup-rewards',
        };
        sendToDataLayer(signupSuccessEvent as unknown as DataLayerItem);
    };

    const onSignUpFormSubmit = async () => {
        try {
            setHasEnrolledInDifferentProgram(false);
            setErrorMessage(null);
            const recaptchaToken = await gRecaptchaManager.executeAndRetrieveToken(appInsights, "SIGNUP") as string;

            const dobFormatted = signUpForm.dob
                ? formatDate(signUpForm.dob, DateFormat.MM_DD_YYYY, DateFormat.YYYY_MM_DD)
                || signUpForm.dob
                : signUpForm.dob;

            const payload = {
                ...signUpForm,
                dob: dobFormatted,
                preferences: mapPreferences(),
                sourceApp: appSource,
                recaptchaToken: recaptchaToken || undefined,
            };

            const loggingPayload = {
                firstName: signUpForm.firstName,
                lastName: signUpForm.lastName,
                emailAddress: signUpForm.emailAddress,
            };
            logging.info(appInsights, 'evt_Signup_req', "SIGNUP_SUBMIT", loggingPayload);

            const response = await createAccount(payload).unwrap();

            const tempValue: string = decrypt(response.value.toString());
            // eslint-disable-next-line
            const data: LoginResponse = JSON.parse(tempValue);

            if (data.header?.status === 'Failure') {
                // eslint-disable-next-line
                const responseMsg: string = data?.header?.message || Constants.CREATE_ACCOUNT_ERROR_MSG;
                // eslint-disable-next-line
                setErrorMessage(responseMsg);
                return;
            }

            appInsights.trackEvent({
                name: 'evt_SignUp_resp',
                properties: {// accepts any type
                    bodyValue: data.aud,
                    user: signUpForm.emailAddress,
                    trace_id: localStorage.getItem('trace_id'),
                },
            });
            logging.info(appInsights, 'evt_SignUp_resp', "SIGNUP_SUCCESS", data.aud);
            sendSignupSuccessEvenToDataLayer();
            setKeyPersist('user', data.aud);
            setKeyPersist('isGuest', "false");
            dispatch(setCredentials({ user: data.aud, token: "", isGuest: false }));
            dispatch(setShowGlobalAlert(true));
            await populateUserDetails(data.aud);
            // createOrderIfExists(isTransitionRequired);

            resetSignupForm();

            dispatch(setWelcomeModalOpen(true));
            dispatch(setRedirectToMyAccount(false));

            // hideModal();
            if (typeof signIn === "function") {
                ga4Layer.pushFormEvent("Rewards");
                signIn(true);
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            // eslint-disable-next-line
            const message = error?.data?.message;
            let errMsg: string = Constants.CREATE_ACCOUNT_ERROR_MSG;
            const err = error as ErrorResponse;

            if (typeof message === "string") {
                errMsg = message;
            } else if (typeof message === "object") {
                errMsg = err?.data?.message?.header?.message || errMsg;
            }

            if (message === Constants.EXISTING_MEMBER_ERROR_MSG) {
                resetSignupForm();
                dispatch(setAlreadySignUpErrorMsg(errMsg));
                if (typeof signIn === "function") {
                    signIn(false);
                }
            } else {
                if (err?.data?.hasEnrolledInDiffrentProgram) {
                    setHasEnrolledInDifferentProgram(true);
                }
                setErrorMessage(errMsg);
            }
        }
    };

    const onSignInKeydown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            signIn(false);
        }
    };

    return (
        <Container fluid className="d-flex align-items-center flex-column my-account mb-4">
            {(isLoading || userDataLoading)
                && (
                <Row className="w-100 mt-2 mb-3">
                    <Col>
                        {(isLoading || userDataLoading) && <LinearProgress className="linear-progress" />}
                    </Col>
                </Row>
                )}

            <Row className="w-100 mb-2">
                <Col className="col-xl-8 offset-xl-2">
                    {errorMessage && (
                        <NotificationBanner message={errorMessage} type={hasEnrolledInDifferentProgram ? "info" : "error"} onClose={() => setErrorMessage(null)} />
                    )}
                </Col>
            </Row>

            <Row className="w-100">
                <Col className="text-center col-md-4 offset-md-4">
                    <Button
                        className="order-now-card-button w-100 mt-0 mb-3"
                        variant="primary"
                        disabled={isSignUpFormInvalid || isLoading || userDataLoading}
                        onClick={onSubmit}
                        tabIndex={0}
                    >
                        JOIN NOW
                    </Button>
                    <p>
                        <span className="font-main">Already have an account?</span>
                        {(isLoading || userDataLoading)
                            ? (<span className="signOn-subText-Light font-sub">  Sign In &gt;</span>)
                            : (
                                /* eslint-disable-next-line */
                                <a className="signOn-subText-Light font-sub sign-in-link" role="link" tabIndex={0} onClick={() => { signIn(false); }} onKeyDown={onSignInKeydown}>  Sign In &gt;</a>)}
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default SignupFooter;
